import { useMemo, useState, Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { IngestedFile, IngestSourceSummary } from '@/hooks/useIngestedDocuments';
import {
  fileStatusChip, filterFiles, isSearchable, providerLabel, type StageFilter,
} from '@/lib/ingestedDocsLogic';
import { useLocalization } from '@/hooks/useLocalization';

interface Props {
  files: IngestedFile[];
  sources: IngestSourceSummary[];
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError?: string | null;
  onLoadMore: () => void;
}

const STAGE_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'active', label: 'All active' },
  { value: 'pending', label: 'Pending' },
  { value: 'extracted', label: 'Extracted' },
  { value: 'analyzed', label: 'Analyzed' },
  { value: 'failed', label: 'Failed' },
  { value: 'unsupported', label: 'Unsupported' },
  { value: 'removed', label: 'Removed' },
  { value: 'all', label: 'Everything' },
];

const typeLabel = (mime: string | null, name: string): string => {
  const ext = name.includes('.') ? name.split('.').pop()!.toUpperCase() : '';
  if (ext && ext.length <= 5) return ext;
  return mime ?? '—';
};

const IngestedFilesTable = ({ files, sources, hasMore, loadingMore, loadMoreError, onLoadMore }: Props) => {
  const { tx, t } = useLocalization();
  const [open, setOpen] = useState(true);
  const [source, setSource] = useState('all');
  const [stage, setStage] = useState<StageFilter>('active');
  const [expanded, setExpanded] = useState<string | null>(null);

  const sourceName = useMemo(() => {
    const m = new Map<string, string>();
    sources.forEach((s) => m.set(s.id, `${providerLabel(s.provider)}: ${s.display_path}`));
    return m;
  }, [sources]);

  const visible = useMemo(() => filterFiles(files, source, stage), [files, source, stage]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="px-1 -ms-1 gap-1 font-semibold" aria-label={t('{0} ingested files', { 0: open ? 'Collapse' : 'Expand' })}>
            {open ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
            
            {t('Ingested files (')}{files.length}{hasMore ? '+' : ''})
          </Button>
        </CollapsibleTrigger>
        {open && (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-8 w-[220px] text-xs" aria-label={t('Filter files by source')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('All sources')}</SelectItem>
                {sources.map((s) => <SelectItem key={s.id} value={s.id}>{sourceName.get(s.id)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stage} onValueChange={(v) => setStage(v as StageFilter)}>
              <SelectTrigger className="h-8 w-[150px] text-xs" aria-label={t('Filter files by status')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{tx(o.label)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <CollapsibleContent>
        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg py-6 text-center">
            {files.length === 0 ? t('No files ingested yet.') : t('No files match these filters.')}
          </p>
        ) : (
          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"><span className="sr-only">{t('Details')}</span></TableHead>
                  <TableHead>{t('File')}</TableHead>
                  <TableHead>{t('Source')}</TableHead>
                  <TableHead>{t('Type')}</TableHead>
                  <TableHead>{t('Status')}</TableHead>
                  <TableHead>{t('Searchable')}</TableHead>
                  <TableHead className="text-right">{t('Candidates')}</TableHead>
                  <TableHead>{t('Updated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((f) => {
                  const chip = fileStatusChip(f);
                  const isOpen = expanded === f.id;
                  const detailId = `ingest-file-detail-${f.id}`;
                  return (
                    <Fragment key={f.id}>
                      <TableRow className={f.removed_at ? 'opacity-60' : undefined}>
                        <TableCell className="p-1">
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7"
                            aria-expanded={isOpen} aria-controls={detailId}
                            aria-label={t('{0} details for {1}', { 0: isOpen ? 'Hide' : 'Show', 1: f.file_name })}
                            onClick={() => setExpanded(isOpen ? null : f.id)}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
                          </Button>
                        </TableCell>
                        <TableCell className="max-w-[240px] truncate text-sm" title={f.file_name}>{f.file_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate" title={sourceName.get(f.source_id) ?? tx(providerLabel(f.provider))}>
                          {tx(providerLabel(f.provider))}
                        </TableCell>
                        <TableCell className="text-xs">{tx(typeLabel(f.mime_type, f.file_name))}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant={chip.tone} className="text-[10px]">{tx(chip.label)}</Badge>
                            {f.truncated && <Badge variant="outline" className="text-[10px]">{t('Truncated')}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {isSearchable(f) ? t('Yes') : (
                            <span className="text-muted-foreground">
                              
                              {t('No')}{f.embedding_status === 'failed' ? t(' (embedding failed)') : ''}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{f.candidate_count}</TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(f.updated_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow id={detailId} className="bg-muted/30 hover:bg-muted/30">
                          <TableCell />
                          <TableCell colSpan={7} className="space-y-1.5 text-xs">
                            {f.source_path && <p className="text-muted-foreground">{t('Folder:')} {f.source_path}</p>}
                            {f.extraction_error && <p className="text-destructive break-words">{t('Error:')} {f.extraction_error}</p>}
                            {f.text_preview
                              ? <p className="whitespace-pre-wrap break-words text-muted-foreground">{f.text_preview}{f.text_preview.length >= 400 ? '…' : ''}</p>
                              : <p className="text-muted-foreground">{t('No text preview available.')}</p>}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {loadMoreError && (
          <p className="pt-2 text-xs text-destructive text-center" role="alert">{loadMoreError}</p>
        )}
        {hasMore && (
          <div className="pt-2 flex justify-center">
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore && <Loader2 className="w-4 h-4 me-2 animate-spin" aria-hidden="true" />}
              {loadMoreError ? t('Retry') : t('Load more')}
            </Button>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default IngestedFilesTable;
